import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bars3Icon,
  XMarkIcon,
  ShoppingCartIcon,
  UserIcon,
  ChevronDownIcon,
  HomeIcon,
  ArrowRightOnRectangleIcon,
  EnvelopeIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, onValue, off, get } from 'firebase/database';
import Swal from 'sweetalert2';
import logo from '../../assets/images/logo.jpeg';

// ─── LOCAL STORAGE HELPERS ───────────────────────────────────────────────────
const LS = {
  get: (key, fb = null) => {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; }
  },
};

const SERVICE_ICONS = {
  'Gym & Fitness': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12h10.5M4.5 9.75h1.5v4.5H4.5zM18 9.75h1.5v4.5H18zM2.25 11.25h2.25v1.5H2.25zM19.5 11.25h2.25v1.5H19.5z" />
    </svg>
  ),
  'Infrared Sauna': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1M4.22 4.22l.707.707m12.728 12.728.707.707M3 12H2m20 0h-1M4.22 19.78l.707-.707M19.07 4.93l-.707.707M12 7a5 5 0 100 10A5 5 0 0012 7z" />
    </svg>
  ),
  'Steam Room': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75c0 0 .75 3 .75 4.5s-.75 2.25-.75 3.75.75 3 .75 4.5M12 3.75c0 0 .75 3 .75 4.5s-.75 2.25-.75 3.75.75 3 .75 4.5M16.5 3.75c0 0 .75 3 .75 4.5s-.75 2.25-.75 3.75.75 3 .75 4.5" />
    </svg>
  ),
  'Classes ': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75c-1.5 0-3 .75-3 2.25v3h6v-3c0-1.5-1.5-2.25-3-2.25zm-3-3l-1.5 2.25M15 9.75l1.5 2.25M9 9.75c0-1.657 1.343-3 3-3s3 1.343 3 3" />
    </svg>
  ),
};

const SERVICE_ACCENTS = {
  'Gym & Fitness':       { accent: '#3B82F6', bg: 'rgba(59,130,246,0.08)',  label: 'STRENGTH' },
  'Infrared Sauna':      { accent: '#F97316', bg: 'rgba(249,115,22,0.08)',  label: 'RECOVERY' },
  'Steam Room':          { accent: '#06B6D4', bg: 'rgba(6,182,212,0.08)',   label: 'RELAX'    },
  'Classes ':      { accent: '#A855F7', bg: 'rgba(168,85,247,0.08)',  label: 'BALANCE'  },
};

// ─── BADGE ───────────────────────────────────────────────────────────────────
const Badge = ({ count, color = '#800020' }) => {
  if (!count || count <= 0) return null;
  return (
    <motion.span
      key={count}
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1,   opacity: 1 }}
      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] text-white text-[10px] font-extrabold rounded-full flex items-center justify-center px-1 shadow-md"
      style={{ background: color, lineHeight: 1 }}
    >
      {count > 99 ? '99+' : count}
    </motion.span>
  );
};

const Navbar = () => {
  const [isOpen,         setIsOpen]         = useState(false);
  const [scrolled,       setScrolled]       = useState(false);
  const [cartCount,      setCartCount]      = useState(0);
  const [wishlistCount,  setWishlistCount]  = useState(0);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});

  const { currentUser, userData, logout, resendVerificationEmail } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const userRole        = userData?.personalInfo?.role     || 'user';
  const userName        = userData?.personalInfo?.name     || currentUser?.displayName || 'User';
  const userEmail       = currentUser?.email               || '';
  const userProvider    = userData?.personalInfo?.provider || 'email';
  const isEmailVerified = userProvider === 'google' ? true : (currentUser?.emailVerified || false);

  const getInitials = () => {
    if (!userName) return '??';
    const parts = userName.split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : userName.substring(0, 2).toUpperCase();
  };

  // ─── SCROLL ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ─── CLOSE MENUS ON ROUTE CHANGE ──────────────────────────────────────────
  useEffect(() => {
    setIsOpen(false);
    setIsUserMenuOpen(false);
    setActiveMegaMenu(null);
    setMobileExpanded({});
  }, [location]);

  // ─── CART & WISHLIST BADGE SYNC ───────────────────────────────────────────
  useEffect(() => {
    let cartUnsub    = null;
    let wishUnsub    = null;
    let pollInterval = null;

    const readFromLS = () => {
      const cart    = LS.get('shop_cart', []);
      const cartQty = cart.reduce((s, i) => s + (i.quantity || 1), 0);
      setCartCount(cartQty);
      const wishlist   = LS.get('wishlist', {});
      const wishCount  = Object.values(wishlist).filter(v => v.isWishlist === true).length;
      setWishlistCount(wishCount);
    };

    if (currentUser) {
      const cartRef = ref(database, `users/${currentUser.uid}/cart`);
      cartUnsub = onValue(cartRef, (snap) => {
        if (!snap.exists()) { setCartCount(0); return; }
        const val = snap.val();
        const arr = Array.isArray(val) ? val : Object.values(val);
        setCartCount(arr.reduce((s, i) => s + (i.quantity || 1), 0));
      });

      const wishRef = ref(database, `users/${currentUser.uid}/wishlist`);
      wishUnsub = onValue(wishRef, (snap) => {
        if (!snap.exists()) { setWishlistCount(0); return; }
        const val   = snap.val();
        const count = Object.values(val).filter(v => v.isWishlist === true).length;
        setWishlistCount(count);
      });
    } else {
      readFromLS();
      pollInterval = setInterval(readFromLS, 2000);
    }

    return () => {
      if (currentUser) {
        if (cartUnsub) off(ref(database, `users/${currentUser.uid}/cart`),    cartUnsub);
        if (wishUnsub) off(ref(database, `users/${currentUser.uid}/wishlist`), wishUnsub);
      }
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentUser]);

  // ─── HANDLERS ──────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      Swal.fire({ icon: 'success', title: 'Logged out', text: 'You have been successfully logged out.', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
      navigate('/');
    } else {
      Swal.fire({ icon: 'error', title: 'Error', text: result.error || 'Failed to logout', timer: 2000, showConfirmButton: false, toast: true, position: 'top-end' });
    }
    setIsUserMenuOpen(false);
  };

  const handleResendVerification = async () => {
    const result = await resendVerificationEmail();
    Swal.fire({
      icon:  result.success ? 'success' : 'error',
      title: result.success ? 'Verification Email Sent' : 'Error',
      text:  result.success ? 'Please check your inbox and spam folder.' : (result.error || 'Failed to send verification email'),
      timer: 3000, showConfirmButton: false, toast: true, position: 'top-end',
    });
  };

  const getDashboardPath = () => {
    if (userRole === 'superAdmin') return '/superadmin';
    if (userRole === 'admin')      return '/admin';
    return '/dashboard';
  };

  const toggleMobileSection = (key) => {
    setMobileExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const navLinks = [
    { name: 'Home',         path: '/'           },
    {
      name: 'Services', path: '/services',
      megaMenu: [
        { title: 'Gym & Fitness',      path: '/services/gym',       description: 'State-of-the-art equipment & free weights' },
        { title: 'Infrared Sauna',     path: '/services/sauna',     description: 'Detox & recovery therapy' },
        { title: 'Steam Room',         path: '/services/steam',     description: 'Relax and rejuvenate' },
        { title: 'Classes ',     path: '/services/yoga',      description: 'Group classes & mind-body wellness' },
      ],
    },
    { name: 'Classes',   path: '/activities' },
    { name: 'Our Facility', path: '/facility'   },
    { name: 'Pricing',      path: '/pricing'    },
    { name: 'Shop',         path: '/shop'       },
    { name: 'About',        path: '/about'      },
    { name: 'Blog',         path: '/blog'       },
    { name: 'Contact',      path: '/contact'    },
  ];

  const getTextColor = () => 'text-gray-700';

  return (
    <nav
      className="fixed w-full backdrop-blur-md shadow-lg py-2 overflow-visible"
      style={{ zIndex: 9999, background: 'rgba(255,255,255,0.95)' }}
    >
      {/* Animated decorative circles */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', top: '-18px', left: '-18px', width: '72px', height: '72px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(128,0,32,0.28) 0%, rgba(128,0,32,0.10) 60%, transparent 80%)', animation: 'navOrb1 6s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '-8px', left: '28px', width: '38px', height: '38px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(160,60,60,0.22) 0%, transparent 70%)', animation: 'navOrb2 8s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '12px', left: '-10px', width: '24px', height: '24px', borderRadius: '50%', border: '1px solid rgba(128,0,32,0.28)', animation: 'navOrbSpin 12s linear infinite' }} />
        <div style={{ position: 'absolute', top: '-22px', right: '-22px', width: '80px', height: '80px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(128,0,32,0.26) 0%, rgba(128,0,32,0.08) 60%, transparent 80%)', animation: 'navOrb3 7s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '4px', right: '32px', width: '30px', height: '30px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(180,80,80,0.20) 0%, transparent 70%)', animation: 'navOrb1 9s ease-in-out infinite reverse' }} />
        <div style={{ position: 'absolute', top: '-6px', right: '8px', width: '20px', height: '20px', borderRadius: '50%', border: '1px solid rgba(128,0,32,0.25)', animation: 'navOrbSpin 16s linear infinite reverse' }} />
        <div style={{ position: 'absolute', top: '14px', right: '-4px', width: '14px', height: '14px', borderRadius: '50%', background: 'rgba(128,0,32,0.18)', animation: 'navOrb2 5s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '50%', left: '18%', width: '6px', height: '6px', borderRadius: '50%', background: 'rgba(128,0,32,0.18)', transform: 'translateY(-50%)', animation: 'navDot 10s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '50%', right: '22%', width: '5px', height: '5px', borderRadius: '50%', background: 'rgba(128,0,32,0.16)', transform: 'translateY(-50%)', animation: 'navDot 13s ease-in-out infinite reverse' }} />
      </div>

      <style>{`
        @keyframes navOrb1   { 0%,100%{transform:translate(0,0) scale(1);opacity:1}50%{transform:translate(6px,5px) scale(1.12);opacity:0.7} }
        @keyframes navOrb2   { 0%,100%{transform:translate(0,0) scale(1);opacity:0.8}40%{transform:translate(-5px,4px) scale(0.9);opacity:1}70%{transform:translate(4px,-3px) scale(1.08);opacity:0.6} }
        @keyframes navOrb3   { 0%,100%{transform:translate(0,0) scale(1);opacity:1}50%{transform:translate(-7px,6px) scale(1.1);opacity:0.65} }
        @keyframes navOrbSpin{ from{transform:rotate(0deg) scale(1);opacity:0.6}50%{transform:rotate(180deg) scale(1.2);opacity:1}to{transform:rotate(360deg) scale(1);opacity:0.6} }
        @keyframes navDot    { 0%,100%{transform:translateY(-50%) scale(1);opacity:0.5}50%{transform:translateY(-60%) scale(1.5);opacity:1} }
      `}</style>

      <div className="container-custom">
        <div className="flex justify-between items-center">

          {/* ── LOGO ──────────────────────────────────────────────────────── */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/40 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
              <img
                src={logo}
                alt="PowerCore Fitness"
                className="h-14 w-auto relative rounded-xl shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105"
              />
            </div>
            {/* Text only shows on desktop (lg+) */}
            <div className="hidden lg:flex flex-col">
              <span className="text-xl font-black tracking-tight text-gray-900">
                POWER<span className="text-primary">CORE</span>
              </span>
              <span className="text-[10px] font-light tracking-[0.3em] uppercase text-gray-500">Fitness</span>
            </div>
          </Link>

          {/* ── DESKTOP NAV LINKS ─────────────────────────────────────────── */}
          <div className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <div
                key={link.path}
                className="relative"
                onMouseEnter={() => link.megaMenu && setActiveMegaMenu(link.name)}
                onMouseLeave={() => setActiveMegaMenu(null)}
              >
                {link.megaMenu ? (
                  <>
                    <button className={`nav-link flex items-center space-x-1 text-center leading-tight ${getTextColor()} hover:text-primary transition-colors`}>
                      <span className="whitespace-pre-line text-sm font-semibold">{link.name}</span>
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${activeMegaMenu === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {activeMegaMenu === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="fixed left-0 right-0 bg-white shadow-2xl border-t border-gray-100"
                          style={{ top: '100%', width: '100vw', zIndex: 99999 }}
                        >
                          <div className="container-custom py-7">
                            <div className="flex items-center gap-3 mb-5">
                              <span style={{ letterSpacing: '0.1em', fontSize: '11px', color: '#999', fontWeight: 600, textTransform: 'uppercase' }}>Our Services</span>
                              <div style={{ flex: 1, height: '1px', background: '#ebebeb' }} />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              {link.megaMenu.map((item) => {
                                const a = SERVICE_ACCENTS[item.title] || { accent: '#888', bg: 'rgba(136,136,136,0.08)', label: '' };
                                return (
                                  <Link key={item.path} to={item.path}
                                    style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', borderRadius: '14px', background: '#f8f8f8', border: '1px solid #ececec', textDecoration: 'none', overflow: 'hidden', transition: 'border-color 0.25s, box-shadow 0.25s, transform 0.2s' }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = `0 8px 32px -4px ${a.accent}44`;
                                      e.currentTarget.style.borderColor = a.accent;
                                      e.currentTarget.style.background = a.bg;
                                      const icon = e.currentTarget.querySelector('.svc-icon');
                                      if (icon) { icon.style.background = a.accent; icon.style.color = '#fff'; icon.style.transform = 'scale(1.08)'; }
                                      const arrow = e.currentTarget.querySelector('.svc-arrow');
                                      if (arrow) { arrow.style.background = a.accent; arrow.style.borderColor = a.accent; arrow.style.color = '#fff'; arrow.style.transform = 'translateX(3px)'; }
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.transform = '';
                                      e.currentTarget.style.boxShadow = '';
                                      e.currentTarget.style.borderColor = '#ececec';
                                      e.currentTarget.style.background = '#f8f8f8';
                                      const icon = e.currentTarget.querySelector('.svc-icon');
                                      if (icon) { icon.style.background = a.bg; icon.style.color = a.accent; icon.style.transform = ''; }
                                      const arrow = e.currentTarget.querySelector('.svc-arrow');
                                      if (arrow) { arrow.style.background = ''; arrow.style.borderColor = '#e5e5e5'; arrow.style.color = '#bbb'; arrow.style.transform = ''; }
                                    }}
                                  >
                                    <div className="svc-icon" style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: a.bg, color: a.accent, flexShrink: 0, transition: 'background 0.25s, transform 0.25s, color 0.25s' }}>
                                      {SERVICE_ICONS[item.title]}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <span style={{ fontSize: 9, letterSpacing: '0.12em', color: a.accent, display: 'block', marginBottom: 2, fontWeight: 700, textTransform: 'uppercase' }}>{a.label}</span>
                                      <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111', display: 'block', lineHeight: 1.2 }}>{item.title}</span>
                                      <span style={{ fontSize: 11.5, color: '#888', marginTop: 2, display: 'block', lineHeight: 1.4 }}>{item.description}</span>
                                    </div>
                                    <span className="svc-arrow" style={{ marginLeft: 'auto', flexShrink: 0, width: 28, height: 28, borderRadius: '50%', border: '1px solid #e5e5e5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12, transition: 'background 0.25s, border-color 0.25s, color 0.25s, transform 0.25s' }}>→</span>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <NavLink to={link.path} className={({ isActive }) => `nav-link relative group text-center leading-tight ${isActive ? 'nav-link-active' : getTextColor()}`}>
                    {({ isActive }) => (
                      <>
                        <span className="text-sm font-semibold block">{link.name}</span>
                        <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-primary transform transition-transform duration-300 ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
                      </>
                    )}
                  </NavLink>
                )}
              </div>
            ))}
          </div>

          {/* ── DESKTOP RIGHT SIDE ────────────────────────────────────────── */}
          <div className="flex items-center space-x-2">

            {/* Cart + Wishlist — desktop only */}
            <div className="hidden lg:flex items-center gap-0.5">
              <Link to="/cart" className="relative p-2 hover:text-primary transition-colors duration-300 group" title="Cart">
                <ShoppingCartIcon className={`h-6 w-6 ${getTextColor()} group-hover:text-primary transition-colors`} />
                <Badge count={cartCount} color="#800020" />
              </Link>
              <Link to="/wishlist" className="relative p-2 hover:text-red-500 transition-colors duration-300 group" title="Wishlist">
                {wishlistCount > 0
                  ? <HeartSolidIcon className="h-6 w-6 text-red-500" />
                  : <HeartIcon className={`h-6 w-6 ${getTextColor()} group-hover:text-red-500 transition-colors`} />
                }
                <Badge count={wishlistCount} color="#ef4444" />
              </Link>
            </div>

            {/* Desktop user menu */}
            <div
              className="relative hidden lg:block"
              onMouseEnter={() => setIsUserMenuOpen(true)}
              onMouseLeave={() => setIsUserMenuOpen(false)}
            >
              {currentUser ? (
                <>
                  {!isEmailVerified && userProvider === 'email' ? (
                    <button onClick={handleResendVerification}
                      className="flex items-center space-x-2 px-3 py-2 bg-yellow-500/20 border border-yellow-500 rounded-lg hover:bg-yellow-500/30 transition-all group">
                      <EnvelopeIcon className="h-5 w-5 text-yellow-500" />
                      <span className="text-xs text-yellow-500 font-medium">Verify Email</span>
                    </button>
                  ) : (
                    <>
                      <button className="flex items-center space-x-2 focus:outline-none group">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white font-bold text-sm shadow-lg group-hover:scale-105 transition-transform">
                            {getInitials()}
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="text-sm font-medium text-gray-700">{userName.split(' ')[0]}</span>
                            <span className="text-xs text-primary font-semibold">
                              {userRole === 'superAdmin' ? 'Super Admin' : userRole === 'admin' ? 'Admin' : 'Member'}
                            </span>
                          </div>
                          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''} text-gray-500`} />
                        </div>
                      </button>

                      <AnimatePresence>
                        {isUserMenuOpen && (
                          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                            className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl py-2 overflow-hidden" style={{ zIndex: 99999 }}>
                            <div className="px-4 py-3 border-b border-gray-100">
                              <div className="flex items-center space-x-3">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white font-bold text-lg">{getInitials()}</div>
                                <div>
                                  <p className="font-semibold text-gray-900">{userName}</p>
                                  <p className="text-xs text-gray-500">{userEmail}</p>
                                  <span className="inline-block text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full mt-1">
                                    {userRole === 'superAdmin' ? 'Super Admin' : userRole === 'admin' ? 'Admin' : 'Member'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <Link to={getDashboardPath()} onClick={() => setIsUserMenuOpen(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors group">
                              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                <HomeIcon className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Dashboard</p>
                                <p className="text-xs text-gray-400">
                                  {userRole === 'superAdmin' ? 'Super Admin Dashboard' : userRole === 'admin' ? 'Admin Dashboard' : 'Member Dashboard'}
                                </p>
                              </div>
                            </Link>
                            <div className="border-t border-gray-100 mt-2 pt-2">
                              <button onClick={handleLogout}
                                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 transition-colors group">
                                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                                  <ArrowRightOnRectangleIcon className="h-4 w-4 text-red-500" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-red-600">Logout</p>
                                  <p className="text-xs text-gray-400">Sign out of your account</p>
                                </div>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </>
              ) : (
                <Link to="/login" className="btn-secondary transform hover:scale-105">Get Started</Link>
              )}
            </div>

            {/* ── MOBILE HEADER: logo image only + cart + wishlist + hamburger ── */}
            <div className="lg:hidden flex items-center gap-1">

              {/* Cart */}
              <Link to="/cart" className="relative p-2 text-gray-700 hover:text-primary transition-colors" title="Cart">
                <ShoppingCartIcon className="h-6 w-6" />
                <Badge count={cartCount} color="#800020" />
              </Link>

              {/* Wishlist */}
              <Link to="/wishlist" className="relative p-2 hover:text-red-500 transition-colors" title="Wishlist">
                {wishlistCount > 0
                  ? <HeartSolidIcon className="h-6 w-6 text-red-500" />
                  : <HeartIcon className="h-6 w-6 text-gray-700" />
                }
                <Badge count={wishlistCount} color="#ef4444" />
              </Link>

              {/* Hamburger toggle */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 transition-colors duration-300 text-gray-700 hover:text-primary"
              >
                {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* ── MOBILE MENU ───────────────────────────────────────────────── */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md rounded-2xl mt-4"
            >
              <div className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 100px)' }}>

                {/* User section */}
                <div className="pb-3 mb-2 border-b border-gray-100">
                  {currentUser ? (
                    <div className="space-y-1">
                      <div className="flex items-center space-x-3 px-3 py-3 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white font-bold text-sm shadow flex-shrink-0">
                          {getInitials()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm truncate">{userName}</p>
                          <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                          <span className="inline-block text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full mt-0.5">
                            {userRole === 'superAdmin' ? 'Super Admin' : userRole === 'admin' ? 'Admin' : 'Member'}
                          </span>
                        </div>
                      </div>

                      {!isEmailVerified && userProvider === 'email' && (
                        <button onClick={handleResendVerification}
                          className="w-full flex items-center space-x-2 px-4 py-2 bg-yellow-500/10 border border-yellow-400 rounded-lg hover:bg-yellow-500/20 transition-all">
                          <EnvelopeIcon className="h-4 w-4 text-yellow-500" />
                          <span className="text-xs text-yellow-600 font-medium">Resend Verification Email</span>
                        </button>
                      )}

                      <Link to={getDashboardPath()} onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-3 px-4 py-2.5 rounded-xl text-gray-700 hover:bg-primary/5 hover:text-primary transition-all">
                        <HomeIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Dashboard</span>
                      </Link>

                      <button onClick={() => { handleLogout(); setIsOpen(false); }}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl text-red-500 hover:bg-red-50 transition-all">
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">Logout</span>
                      </button>
                    </div>
                  ) : (
                    <Link to="/login" onClick={() => setIsOpen(false)}
                      className="block w-full text-center py-3 px-4 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-all shadow">
                      Get Started
                    </Link>
                  )}
                </div>

                {/* Nav links */}
                {navLinks.map((link) => (
                  <div key={link.path}>
                    {link.megaMenu ? (
                      <div>
                        <button
                          onClick={() => toggleMobileSection(link.name)}
                          className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-gray-700 hover:bg-primary/5 hover:text-primary transition-all"
                        >
                          <span className="text-sm font-semibold">{link.name}</span>
                          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${mobileExpanded[link.name] ? 'rotate-180' : ''}`} />
                        </button>

                        <AnimatePresence>
                          {mobileExpanded[link.name] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="pl-4 pb-1 space-y-0.5">
                                {link.megaMenu.map((item) => {
                                  const a = SERVICE_ACCENTS[item.title] || { accent: '#888' };
                                  return (
                                    <Link
                                      key={item.path}
                                      to={item.path}
                                      onClick={() => setIsOpen(false)}
                                      className="flex items-center gap-3 py-2.5 px-3 rounded-xl transition-all hover:bg-gray-50"
                                    >
                                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ background: a.bg || 'rgba(128,0,32,0.08)', color: a.accent }}>
                                        <div className="scale-75">{SERVICE_ICONS[item.title]}</div>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-700 truncate">{item.title}</p>
                                        <p className="text-xs text-gray-400 truncate">{item.description}</p>
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <NavLink
                        to={link.path}
                        className={({ isActive }) =>
                          `block py-2.5 px-4 rounded-xl transition-all text-sm font-medium ${isActive ? 'bg-primary text-white' : 'text-gray-700 hover:bg-primary/10 hover:text-primary'}`
                        }
                        onClick={() => setIsOpen(false)}
                      >
                        {link.name}
                      </NavLink>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;